import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { toPng } from "html-to-image";
import { Download, Share2, MapPin, Users as UsersIcon, Calendar, Clock, Moon, Ticket as TicketIcon, Sparkles, CheckCircle2, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return {
      date: d.toLocaleDateString("ar-SA-u-nu-latn", { weekday: "long", year: "numeric", month: "long", day: "numeric" }),
      time: d.toLocaleTimeString("ar-SA-u-nu-latn", { hour: "2-digit", minute: "2-digit" }),
      short: d.toLocaleDateString("ar-SA-u-nu-latn", { day: "2-digit", month: "short" }),
    };
  } catch { return null; }
};

export default function TicketPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const ticketRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/public/ticket/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "تعذّر فتح التذكرة"));
  }, [token]);

  const download = async () => {
    if (!ticketRef.current) return;
    try {
      const dataUrl = await toPng(ticketRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#FDFBF7" });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `ticket-${data.ticket.ref_no}.png`;
      a.click();
      toast.success("تم تحميل التذكرة");
    } catch (e) {
      toast.error("تعذّر التحميل");
    }
  };

  const share = async () => {
    const url = window.location.href;
    const text = `تذكرتي في ${data?.campaign?.name || "الحملة"} — ${data?.association?.name}`;
    if (navigator.share) {
      try { await navigator.share({ title: "تذكرة عمرة", text, url }); } catch { /* user cancelled */ }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("تم نسخ رابط التذكرة");
    }
  };

  if (error) {
    return (
      <FullScreen>
        <div className="max-w-md text-center bg-white rounded-2xl p-10 shadow-xl border border-red-200">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
            <TicketIcon className="h-8 w-8 text-red-500" />
          </div>
          <h2 className="text-2xl font-black text-red-700 mb-2">تعذّر عرض التذكرة</h2>
          <p className="text-slate-600">{error}</p>
        </div>
      </FullScreen>
    );
  }

  if (!data) return <FullScreen><div className="text-emerald-900">جارٍ تجهيز تذكرتك...</div></FullScreen>;

  const { ticket, campaign, association } = data;
  const att = formatDate(campaign.attendance_at);
  const qrPayload = JSON.stringify({ ref: ticket.ref_no, name: ticket.name, id: ticket.national_id, campaign: campaign.name });

  return (
    <div className="min-h-screen py-8 px-4 flex flex-col items-center bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-800 relative overflow-hidden" dir="rtl">
      {/* Ambient decorative pattern */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 25% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 75% 80%, #fff 1px, transparent 1px)", backgroundSize: "48px 48px" }} />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full bg-emerald-400/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-2xl mb-6 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-amber-200 text-xs font-bold mb-3">
          <Sparkles className="h-3.5 w-3.5" /> تذكرتك جاهزة
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black text-white mb-1">تذكرة عمرة رقمية</h1>
        <p className="text-emerald-100/70 text-sm">احفظها في هاتفك واعرضها لمشرف الرحلة عند الحضور</p>
      </div>

      {/* THE TICKET */}
      <div ref={ticketRef} data-testid="ticket-card" className="relative z-10 w-full max-w-2xl">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] shadow-2xl shadow-black/40 rounded-2xl overflow-hidden">
          {/* Main body */}
          <div className="relative bg-gradient-to-br from-emerald-950 via-emerald-900 to-emerald-800 text-white p-7">
            {/* Corner ornament */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-amber-400/20 to-transparent rounded-full blur-2xl pointer-events-none" />

            {/* Header row */}
            <div className="flex items-center gap-3 pb-5 border-b border-white/15">
              {association.logo_base64 ? (
                <img src={association.logo_base64} alt="" className="h-14 w-14 rounded-xl object-cover ring-2 ring-amber-400/50" />
              ) : (
                <div className="h-14 w-14 rounded-xl bg-amber-500 flex items-center justify-center">
                  <Moon className="h-8 w-8 text-emerald-950" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-amber-300/80 tracking-[0.2em] font-bold">DIGITAL BOARDING PASS</div>
                <div className="font-heading font-black text-lg truncate">{association.name}</div>
              </div>
              <div className="text-left flex-shrink-0">
                <div className="text-[10px] text-amber-200/70 mb-0.5">رقم التذكرة</div>
                <div data-testid="ticket-ref-no" className="font-mono font-black text-lg text-amber-300 tabular-nums">#{ticket.ref_no}</div>
              </div>
            </div>

            {/* Campaign name */}
            <div className="mt-5 mb-6">
              <div className="text-[10px] text-amber-200/70 tracking-widest font-bold mb-1">حملة العمرة</div>
              <div data-testid="ticket-campaign-name" className="font-heading text-2xl md:text-3xl font-black leading-tight">
                {campaign.name || "—"}
              </div>
              {campaign.number && (
                <div className="text-emerald-100/60 text-xs mt-1">حملة رقم #{campaign.number}</div>
              )}
            </div>

            {/* Passenger + companions */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-[10px] text-amber-200/70 tracking-widest font-bold mb-1">اسم المعتمر</div>
                <div data-testid="ticket-name" className="font-heading font-black text-lg truncate">{ticket.name}</div>
              </div>
              <div>
                <div className="text-[10px] text-amber-200/70 tracking-widest font-bold mb-1">المرافقون</div>
                <div className="font-heading font-black text-lg flex items-center gap-2">
                  <UsersIcon className="h-4 w-4 text-amber-300" />
                  {ticket.companions_count > 0 ? `+${ticket.companions_count} ${ticket.companions_count === 1 ? "مرافق" : "مرافقين"}` : "بدون"}
                </div>
              </div>
            </div>

            {/* Attendance date/time — the hero */}
            <div className="grid grid-cols-2 gap-4 p-4 rounded-xl bg-white/5 backdrop-blur border border-white/10 mb-5">
              <div>
                <div className="flex items-center gap-1.5 text-[10px] text-amber-200/70 tracking-widest font-bold mb-1.5">
                  <Calendar className="h-3 w-3" /> تاريخ الحضور
                </div>
                <div data-testid="ticket-date" className="font-heading font-black text-base">
                  {att?.date || "—"}
                </div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-[10px] text-amber-200/70 tracking-widest font-bold mb-1.5">
                  <Clock className="h-3 w-3" /> وقت الحضور
                </div>
                <div data-testid="ticket-time" className="font-heading font-black text-2xl tabular-nums tracking-wider">
                  {att?.time || "—"}
                </div>
              </div>
            </div>

            {/* Room + private badge */}
            <div className="flex flex-wrap items-center gap-2">
              {ticket.room_number && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-100 text-xs font-bold border border-emerald-400/30">
                  <MapPin className="h-3 w-3" /> غرفة {ticket.room_number}
                </div>
              )}
              {ticket.private_room && (
                <div data-testid="ticket-private-badge" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/20 text-amber-200 text-xs font-bold border border-amber-400/30">
                  <Sparkles className="h-3 w-3" /> غرفة خاصة
                </div>
              )}
              {ticket.registration_kind === "family" && (
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white/90 text-xs font-bold border border-white/15">
                  <UsersIcon className="h-3 w-3" /> تسجيل عائلي
                </div>
              )}
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-400/20 text-emerald-100 text-xs font-bold border border-emerald-400/30">
                <CheckCircle2 className="h-3 w-3" /> مؤكدة
              </div>
            </div>
          </div>

          {/* Perforation + QR panel */}
          <div className="relative bg-gradient-to-br from-amber-50 to-amber-100 p-5 flex flex-col items-center justify-center">
            {/* Perforation line (vertical on desktop, horizontal on mobile) */}
            <div className="hidden md:block absolute inset-y-0 -right-[1px] w-px pointer-events-none"
              style={{ backgroundImage: "repeating-linear-gradient(to bottom, rgba(255,255,255,0.7) 0 6px, transparent 6px 12px)" }} />
            <div className="md:hidden absolute inset-x-0 -top-[1px] h-px pointer-events-none"
              style={{ backgroundImage: "repeating-linear-gradient(to right, rgba(6,78,59,0.35) 0 6px, transparent 6px 12px)" }} />
            {/* Punch holes */}
            <div className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-emerald-950" />
            <div className="hidden md:block absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-amber-100" />
            <div className="md:hidden absolute -top-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-emerald-950" />

            <div className="text-[10px] text-emerald-900/60 tracking-widest font-bold mb-2">SCAN QR</div>
            <div className="bg-white rounded-xl p-3 shadow-md" data-testid="ticket-qr">
              <QRCodeSVG value={qrPayload} size={140} bgColor="#ffffff" fgColor="#064E3B" level="M" includeMargin={false} />
            </div>
            <div className="mt-3 text-center">
              <div className="text-[9px] text-emerald-900/60 tracking-widest font-bold">TICKET NO.</div>
              <div className="font-mono font-black text-emerald-950 tabular-nums text-sm">#{ticket.ref_no}</div>
            </div>
          </div>
        </div>

        {/* Small footer strip */}
        <div className="mt-3 flex items-center justify-between text-white/60 text-[11px] px-1">
          <span>يرجى تقديم هذه التذكرة عند الحضور</span>
          {association.support_phone && <span dir="ltr">استفسار: {association.support_phone}</span>}
        </div>
      </div>

      {/* Actions */}
      <div className="relative z-10 mt-8 flex flex-wrap gap-3 justify-center">
        {campaign.whatsapp_group_link && (
          <a href={campaign.whatsapp_group_link} target="_blank" rel="noreferrer" data-testid="ticket-whatsapp-btn"
            className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold shadow-lg shadow-emerald-500/20 transition-all">
            <MessageCircle className="h-4 w-4" /> انضم لمجموعة الحملة
          </a>
        )}
        <Button data-testid="ticket-download-btn" onClick={download}
          className="bg-amber-500 hover:bg-amber-400 text-emerald-950 font-bold gap-2 h-12 px-6 rounded-full shadow-lg shadow-amber-500/30">
          <Download className="h-4 w-4" /> تحميل كصورة
        </Button>
        <Button data-testid="ticket-share-btn" onClick={share} variant="outline"
          className="bg-white/10 backdrop-blur border-white/25 text-white hover:bg-white/20 gap-2 h-12 px-6 rounded-full">
          <Share2 className="h-4 w-4" /> مشاركة
        </Button>
      </div>

      <div className="relative z-10 mt-6 text-white/40 text-xs">
        © {new Date().getFullYear()} {association.name}
      </div>
    </div>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-950 to-emerald-900 flex items-center justify-center p-4" dir="rtl">
      {children}
    </div>
  );
}
